import { TokenResponse, useGoogleLogin } from '@react-oauth/google'
import { Button, Input } from 'components'
import { ChangeEvent, FormEvent } from 'react'

interface BaseLoginFormViewProps {
  email: string
  validEmail: boolean
  isRequesting: boolean
  error: string | undefined
  onEmailChange: (e: ChangeEvent<HTMLInputElement>) => void
  onSubmit: (e: FormEvent) => void
  onGoogleClick: () => void
  onGoogleLogin: (tokenResponse: TokenResponse) => void
  onGoogleError: () => void
}

const GoogleGIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"
    />
  </svg>
)

/**
 * Email + Google login form on the Taka design system.
 *
 * Voice is unified across all flows here — `Sign in or create an account.` then
 * `Continue`. Per-flow personalisation lives in the left-side editorial hero.
 */
export const BaseLoginFormView: React.FC<BaseLoginFormViewProps> = ({
  email,
  validEmail,
  isRequesting,
  error,
  onEmailChange,
  onSubmit,
  onGoogleClick,
  onGoogleLogin,
  onGoogleError,
}) => {
  const triggerGoogleLogin = useGoogleLogin({
    flow: 'implicit',
    scope: 'openid email profile',
    onSuccess: onGoogleLogin,
    onError: onGoogleError,
  })

  const handleGoogleClick = () => {
    onGoogleClick()
    triggerGoogleLogin()
  }

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col gap-2">
        <span className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
          Welcome
        </span>
        <h2 className="text-[28px] leading-[1.1] font-semibold tracking-[-0.02em] text-paper">
          Sign in or create an account.
        </h2>
        <p className="lede !text-[15px] !leading-[1.55]">
          Use your email or Google account to continue.
        </p>
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
        <Input
          id="email"
          type="email"
          name="email"
          variant="dark"
          value={email}
          onChange={onEmailChange}
          placeholder="you@business.com"
          required
          disabled={isRequesting}
          aria-invalid={!!error}
          autoComplete="email"
          autoFocus
        />

        <Button
          type="submit"
          variant="t3-primary"
          size="t3-lg"
          disabled={isRequesting || !validEmail}
          aria-busy={isRequesting}
          data-testid="login-submit"
        >
          {isRequesting ? 'Sending code…' : 'Continue →'}
        </Button>

        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
      </form>

      <div className="flex items-center gap-4">
        <div className="h-px flex-1 bg-line" />
        <span className="mono text-[10px] tracking-[0.18em] text-paper-faint uppercase">or</span>
        <div className="h-px flex-1 bg-line" />
      </div>

      <Button
        type="button"
        variant="t3-tertiary"
        size="t3-lg"
        onClick={handleGoogleClick}
        disabled={isRequesting}
        data-testid="login-google"
      >
        <GoogleGIcon />
        Continue with Google
      </Button>

      <p className="mt-1 text-center text-[11px] leading-relaxed text-paper-faint">
        By continuing, you agree to <br className="sm:hidden" />
        the{' '}
        <a
          href="https://taka.ai/terms"
          className="link-muted"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Service
        </a>{' '}
        and{' '}
        <a
          href="https://taka.ai/privacy"
          className="link-muted"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </a>
        .
      </p>
    </div>
  )
}
